import React, { Component } from 'react';
import { connect } from 'react-redux';

import lodashGet from 'lodash/get';
import lodashSet from 'lodash/set';
import lodashFind from 'lodash/find';
import lodashIsNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';

import { Alert, Autocomplete, Box, Button, Card, CircularProgress, FormControl, Grid, InputAdornment, Stack, TableContainer, TextField, Tooltip, Typography } from '@mui/material';
import CustomDataGrid from '../../customDataGrid';
import { ThumbnailParcelShapeFromPathInfos } from '../../thumbnail/ThumbnailParcelShape';
import CustomDatePicker from '../../customDatePicker';
import LinkToContactUs from '../../linkToContactUs';
import AlertDialog from '../../alertDialog';

import { enumCultureFertilizer } from '../../../models/constantEnumCulture';

import Calculate from '@mui/icons-material/Calculate';
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import HelpIcon from '@mui/icons-material/Help';
import ErrorOutline from '@mui/icons-material/ErrorOutline';

import { ActionGoToExternalStep, LastNitrogenInputExternalSteps, ActionSaveExternalSpecificInfos, ActionClearLastNitrogenInputError } from '../../../redux/actions/lastNitrogenInput';
import { TableType } from '../../../redux/actions/settings';
import { ActionShowProfilMenuDialog, ProfilIndex } from '../../../redux/actions/contextApp';
import { CodeObjectifRendementDefault, CodeObjectifRendementProtein, FertilizerEngineEnum, FertilizerEngineEnumValue, ActionLoadVaritiesForExternalSpecificInfosFromParcel } from '../../../redux/actions/fertilizer';

import { ParcelsHelper } from '../../../utils/parcelsHelper';
import DateHelper from '../../../utils/dateHelper';
import stringHelper from '../../../utils/stringHelper';
import FertilizerHelper from '../../../utils/fertilizerHelper';
import ConstantsFertilizer from '../../../utils/constantsFertilizer';

import StringTranslate from '../../../assets/i18n/stringLanguage';

import getTheme from "../../../themes/index.js";
import NumberField from '../../customComponents/NumberField';

let theme = getTheme();


class LastNitrogenInputExternalInfosManagement extends Component {

    constructor(props) {
        super(props);

        this.currentCropYear = FertilizerHelper.getCurrentCropYear(props.closingDateAzofert, props.openingDateAzofert); //Année de campagne en cours
        this.minSowingDate = new Date(this.currentCropYear - 1, 7, 1); // 1er aout (en js les mois commencent à 0)
        this.maxSowingDate = new Date(this.currentCropYear - 1, 11, 31); // 31 décembre
        this.minOlympicPerformance = 0;
        this.maxOlympicPerformance = 140;
        this.minDoseTotal = 0;
        this.maxDoseTotal = 300;
        this.minSowingDateString = `${this.minSowingDate.getDate()}/${this.minSowingDate.getMonth() + 1}`;
        this.maxSowingDateString = `${this.maxSowingDate.getDate()}/${this.maxSowingDate.getMonth() + 1}`;
        
        this.datasErrors = {}; // dico<parcelId, objectError>: dictionnaire permettant de savoir quelle donnée de chaque parcelle est en erreur
        // Indique les messages d'erreur, liés à la saisie client, à afficher
        this.errorMessages = {
            missingData: false,
            errorSowingDate: false,
            errorPerformanceGoal: false,
            errorDoseTotal: false,
            errorDosePreviouslyInput: false,
        }


		const errorMessage = lodashGet(props, 'errorMessage', undefined);

        this.state = {
            datas: this.initialiseDatas(props.parcelIdsSelected), //lignes des parcelles

            // Indique si l'on doit afficher les messages d'erreur ou non
            showErrorMessages: false, 
            generateResults: false, // indique qu'on a lancé la génération d'au moins dernier apport
            showFindResultMessage: false, // indique si on doit afficher le message pour indiquer où retrouver les résultats

            // Affiche la pop-up d'erreur de génération
			openDialogOfErrors: (errorMessage !== undefined),

            // Gestion de la saisie commune du rendement olympique
            commonPerformance: null,
            commonPerformanceError: false,
        }
        
        // liste des noms de colonnes affichées
        this.columns = this.initialiseColumns(props.parcelIdsSelected); //RQ: pas dans le 'state' car on en utilise des propriétés !

        // Définition du popup contenant le message d'erreur à diffuser au client:
        this.popupErrorDialog = {
            getTitle: () => { return (<ErrorOutline />); },
            description: errorMessage, //Rq : sera mis à jour en fonction de la prop 'errorMessage' !
            getAdditionalDescription: () => {
                return (
                    <Typography variant="subtitle1">
                        {StringTranslate.errorDialogText2} <LinkToContactUs displayText={StringTranslate.contactUs} />. 
                    </Typography>
                );
            },
            button: StringTranslate.close,
        };

        this.CustomToolbar = this.CustomToolbar.bind(this);
    }

    componentDidMount() {
        //Permet de récupérer les variétés pour chaque parcelle dès le départ si on les retrouve
        this.updateMatchedVarieties();
    }
      
    componentDidUpdate(prevProps, prevState) {
        const { errorMessage, productionTargetsList, varietiesList, parcelIdsSelected } = this.props;
        const { generateResults, datas } = this.state;

        if (((!prevProps) || (!prevProps.errorMessage) || (prevProps.errorMessage === '') || (prevProps.errorMessage === undefined)) &&
			(errorMessage && (errorMessage !== '') && (errorMessage !== undefined))) {
			this.popupErrorDialog.description = errorMessage;

			this.setState({
				openDialogOfErrors: true,
			});
		}

        let previousNbParcelIdsSelected = lodashGet(prevProps, 'parcelIdsSelected.length', 0);        
        let nbParcelIdsSelected = lodashGet(parcelIdsSelected, 'length', 0);
        

        // Suite à la récupération des types de production
        if (!isEqual(prevProps.productionTargetsList, productionTargetsList)) {
            // On recharge suite à la modification des listes (pour mettre à jour les options)
            this.columns = this.initialiseColumns(parcelIdsSelected);
            //On remet à jour le match de variétés pour sélectionner automatiquement l'objectif de production en fonction de la variété récupérée
            const newDatas = this.initialiseDatas(parcelIdsSelected);

            this.updateMatchedVarieties(newDatas);
        }

        // Suite à la récupération des listes de variétés
        if (prevProps.varietiesList !== varietiesList)
        {
            // On recharge suite à la modification des listes (pour mettre à jour les options)
            this.columns = this.initialiseColumns(parcelIdsSelected);

            const newDatas = this.initialiseDatas(parcelIdsSelected);

            this.setState({
                datas: newDatas,
            });
        }

        
        // suite au calul de dernier apport externe
        if (previousNbParcelIdsSelected !== nbParcelIdsSelected)
        {
            // On retire les données qui ne sont plus à afficher
            const newDatas = datas.filter((data) => {
                return parcelIdsSelected.includes(data.id);
            });

            let showFindResultMessage = false;
            if (generateResults)
                showFindResultMessage = true;

            this.setState({
                datas: newDatas,
                showFindResultMessage: showFindResultMessage,
                generateResults: false,
            });
        }
    }

    /**
     * Méthode pour récupérer la variété en base de données par rapport à celle de la parcelle s'il y a une correspondance
     * @param {*} updatedDatas Données du tableau
     * @returns La liste des données mise à jour
     */
    matchVarieties() {
        const { loadVaritiesFromParcel, parcelIdsSelected } = this.props;

        // Tableau temporaire pour stocker les réponses API avant mise à jour
        const apiResponses = [];

        // Pour chaque parcelle, on effectue l'appel API pour récupérer la variété correspondante
        const promises = parcelIdsSelected.map(parcelId => {
          return loadVaritiesFromParcel(parcelId)
            .then(respDatas => {
                if (respDatas) {
                    // Stocke la réponse dans le tableau temporaire
                    apiResponses.push({ parcelId, respDatas });
                }
            })
            .catch(() => Promise.resolve()); // On ignore l'erreur si aucune correspondance n'est trouvée et on renvoie une promesse résolue pour permettre à Promise.all de continuer
        });
        //Une fois que toutes les promesses sont résolues, on retourne la liste mise à jour
        return Promise.all(promises).then(() => {
            this.applyVarietyUpdates(apiResponses);
        });    
    }

    /**
     * Méthode pour mettre à jour le matchingVarieties en cas d'obtention de certaines listes 
     * 
     */
    updateMatchedVarieties() {
        const { parcelIdsSelected } = this.props;
        const baseDatas = this.initialiseDatas(parcelIdsSelected);
    
        this.setState({ datas: baseDatas }, () => {
            this.matchVarieties(); // Une fois que les données de base sont mises à jour, on lance les requêtes API
        });
    }

    applyVarietyUpdates(apiResponses) {
        const { productionTargetsList } = this.props;
        this.setState(prevState => {
            let newDatas = [...prevState.datas];
    
            apiResponses.forEach(({ parcelId, respDatas }) => {
                const index = newDatas.findIndex(data => data.parcel.id === parcelId);
                if (index !== -1) {
                    if (!newDatas[index].variety) { 
                        newDatas[index] = {
                            ...newDatas[index],
                            variety: respDatas,
                        };
    
                        const isImprovingCrop = lodashGet(respDatas, 'isImprovingCrop', false);
                        newDatas[index].isImprovingCrop = isImprovingCrop;
    
                        const prodTargetRendement = lodashFind(productionTargetsList, pt => pt.code === CodeObjectifRendementDefault);
                        const prodTargetProteine = lodashFind(productionTargetsList, pt => pt.code === CodeObjectifRendementProtein);
    
                        if (isImprovingCrop && prodTargetProteine) {
                            newDatas[index].productionTarget = prodTargetProteine;
                        } else if (!isImprovingCrop && prodTargetRendement) {
                            newDatas[index].productionTarget = prodTargetRendement;
                        }
    
                        lodashSet(this.datasErrors, `${parcelId}.productionTarget`, false);
                    }
                }
            });
    
            return { datas: newDatas };
        });
    }
    

    /**
     * Fonction permettant de créer les colonnes à afficher
     * @param {number[]} parcelIdFilteredList 
     * @returns {columns[]} liste des colonnes du tableau à afficher
     */
    initialiseColumns(parcelIdFilteredList) {
        const { productionTargetsList, varietiesList, loadingVarietiesList, loadingCommonLists, } = this.props;

        let newColumns = [];
        if (parcelIdFilteredList && parcelIdFilteredList[0]) {

            /* ↓ ajout colonne visible - (Miniature + nom de la parcelle) ↓ */
            newColumns.push({
                headerName: `${StringTranslate.nomcolumn}`,
                field: "nom",
                minWidth: 180,
                hideable: false,
                renderHeader: (params) => {
                    return (<Typography fontWeight='bold' >{StringTranslate.nomcolumn}</Typography>)
                },
                renderCell: params => {
                    return (
                        <Box style={{ display: "flex", flexDirection: "row", alignItems: "center" }} >
                            {(params) ? (<ThumbnailParcelShapeFromPathInfos id={`listItemTbl_${params.row.thumbnailInfos.parcelId}`} {...params.row.thumbnailInfos} />) : (undefined)}
                            <Typography>{params.value}</Typography>
                        </Box>
                    )
                }
            });

            // Choix de variété
            newColumns.push({
                headerName: `${StringTranslate.libelecolumnvariete}`,
                field: "variety",
                minWidth: 180,
                hideable: false,
                disableColumnMenu: true,
                sortable: false,
                renderHeader: (params) => {
                    return (<Typography fontWeight='bold' >{`${StringTranslate.libelecolumnvariete}*`}</Typography>)
                },
                renderCell: params => {
                    let loading = (loadingVarietiesList === true);
                    return (
                        <Box
                            style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                            sx={{ width: "100%" }}
                        >
                            <FormControl fullWidth>
                                <Autocomplete
                                    fullWidth
                                    id="select-variety-id"
                                    size='small'
                                    disableClearable
                                    disableListWrap
                                    value={loading? null : params.value}
                                    onChange={(event, newValue) => this.handleChangeValue(params.id, params.field, newValue)}
                                    disabled={((varietiesList === undefined) || (varietiesList.length <= 0))}
                                    getOptionLabel={(value) => value.label}
                                    getOptionKey={(value) => value.id}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    options={varietiesList.sort((a, b) => a.label.localeCompare(b.label))}
                                    loading={loading}
                                    loadingText={StringTranslate.loading}
                                    renderInput={(inputParams) => {
                                        //const loading = this.state.loadingVarieties.includes(params.row.parcelId); 

                                        return (
                                            <TextField
                                                {...inputParams}
                                                size="small"
                                                label=""
                                                placeholder={StringTranslate.selectItem}
                                                /* Le composant <Autocomplete /> permet généralement de saisir des espaces dans le champ de saisie. 
                                                Ici, il est ajouté dans une définition de colonne <DataGrid /> via renderCell, 
                                                tout fonctionne à l'exception de la possibilité de saisir un espace, ce qui empêche le filtre.
                                                D'où la nécessité de rajouter "onKeyDown" : */
                                                onKeyDown={(e) => {
                                                    e.key === " " && e.stopPropagation();
                                                    }}
                                                InputProps={{
                                                    ...inputParams.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {loading ? <CircularProgress color="primary" size={20} /> : null}
                                                            {inputParams.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    )
                                                }}
                                                error={lodashGet(params, `row.errors.${params.field}`, false)}
                                            />
                                        )
                                    }}
                                /> 
                            </FormControl>
                        </Box>
                    )
                }
            });

            // Choix de type de production
            newColumns.push({
                headerName: `${StringTranslate.productionTarget}`,
                field: 'productionTarget',
				minWidth: 180,
				hideable: false,
                disableColumnMenu: true,
                sortable: false,
				renderHeader: (params) => {
					return (<Typography sx={{ textWrap: 'wrap', textAlign: 'start' }} fontWeight='bold'>{`${StringTranslate.productionTarget}*`}</Typography>)
				},
                renderCell: params => {
                    let loading = (loadingCommonLists === true);
                    return (
                        <Box
                            style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                            sx={{ width: "100%" }}>
                            <FormControl fullWidth>
                                <Autocomplete
                                    fullWidth
                                    id="select-production-target"
                                    size='small'
                                    disableClearable
                                    disableListWrap
                                    value={loading? null : params.value}
                                    onChange={(event, newValue) => this.handleChangeValue(params.id, params.field, newValue)}
                                    disabled={(params.row.isImprovingCrop || (productionTargetsList === undefined) || (productionTargetsList.length <= 0))}
                                    getOptionLabel={(value) => value.label}
                                    getOptionKey={(value) => value.id}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    options={productionTargetsList}
                                    loading={loading}
                                    loadingText={StringTranslate.loading}
                                    renderInput={(inputParams) => {
                                        //const loading = this.state.loadingVarieties.includes(params.row.parcelId); 

                                        return (
                                            <TextField
                                                {...inputParams}
                                                size="small"
                                                label=""
                                                placeholder={StringTranslate.selectItem}
                                                /* Le composant <Autocomplete /> permet généralement de saisir des espaces dans le champ de saisie. 
                                                Ici, il est ajouté dans une définition de colonne <DataGrid /> via renderCell, 
                                                tout fonctionne à l'exception de la possibilité de saisir un espace, ce qui empêche le filtre.
                                                D'où la nécessité de rajouter "onKeyDown" : */
                                                onKeyDown={(e) => {
                                                    e.key === " " && e.stopPropagation();
                                                    }}
                                                InputProps={{
                                                    ...inputParams.InputProps,
                                                    endAdornment: (
                                                        <React.Fragment>
                                                            {loading ? <CircularProgress color="primary" size={20} /> : null}
                                                            {inputParams.InputProps.endAdornment}
                                                        </React.Fragment>
                                                    )
                                                }}
                                                error={lodashGet(params, `row.errors.${params.field}`, false)}
                                            />
                                        )
                                    }}
                                /> 
                            </FormControl>
                        </Box>
                    )
                }
            });

            // Saisie de date de semis
            newColumns.push({
                headerName: `${StringTranslate.libelecolumnsemis}`,
                field: "sowingDate",
                width: 170,
                type: "date",
                hideable: false,
                disableColumnMenu: true,
                sortable: false,
                renderHeader: (params) => {
                    return (<Typography fontWeight='bold' >{`${StringTranslate.libelecolumnsemis}*`}</Typography>)
                },
                renderCell: params => {
                    return (
                        <Box
                            style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                            sx={{ width: "100%" }}>
                            <FormControl>
                                <CustomDatePicker
                                    label={StringTranslate.libeleColumn}
                                    value={((params.value !== null) && (params.value !== undefined) && (params.value !== '')) ? new Date(params.value) : null}
                                    onChange={(newValue) => this.handleChangeValue(params.id, params.field, newValue)}
                                    placeholder={StringTranslate.formatDatePlaceHolder}
                                    minDate={this.minSowingDate}
                                    maxDate={this.maxSowingDate}
                                    size={'small'}
                                    error={lodashGet(params, `row.errors.${params.field}`, false)}
                                />
                            </FormControl>
                        </Box>
                    )
                }
            });

            // Saisie du rendement olympique
            newColumns.push({
                headerName: `${StringTranslate.performanceObjective}`,
                field: 'performanceGoal',
				minWidth: 150,
				hideable: false,
                disableColumnMenu: true,
                sortable: false,
                renderHeader: (params) => {
                    return (<>
                            <Typography sx={{ textWrap: 'wrap', textAlign: 'start' }} fontWeight='bold'>{`${StringTranslate.olympicPerformance}*`}</Typography>
                            <Tooltip title={StringTranslate.olympicPerformanceHelper} arrow >
                                <HelpIcon />
                            </Tooltip>
                        </>);
				},
                renderCell: params => {
                    return (
                        <Box
                            style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                            sx={{ width: "100%" }}>
                            <FormControl>
                                <NumberField
                                    required
                                    id="fumure_ExternalInfos_objRendement"
                                    value={params.value}
                                    onChange={(newValue) => this.handleChangeValue(params.id, params.field, newValue)}
                                    type="uInteger"
                                    InputProps={{ endAdornment: <InputAdornment position="end">q/ha</InputAdornment> }}
                                    onWheel={event => event.target.blur()}
                                    size='small'
                                    error={lodashGet(params, `row.errors.${params.field}`, false)}
                                />
                            </FormControl>
                        </Box>
                    )
                }
            });

            // Saisie de la dose total du PPF externe
            newColumns.push({
                hearderName: `${StringTranslate.totalNdose}`,
                field: 'nitrogenPrescription',
				minWidth: 150,
				hideable: false,
                disableColumnMenu: true,
                sortable: false,
				renderHeader: (params) => {
					return (<Typography sx={{ textWrap: 'wrap', textAlign: 'start' }} fontWeight='bold'>{`${StringTranslate.totalNdose}*`}</Typography>)
				},
                renderCell: params => {
                    return (
                        <Box
                            style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                            sx={{ width: "100%" }}>
                            <FormControl>
                                <NumberField
                                    required
                                    id="fumure_ExternalInfos_nitrogenPrescription"
                                    value={params.value}
                                    onChange={(newValue) => this.handleChangeValue(params.id, params.field, newValue)}
                                    type="uInteger"
                                    InputProps={{ endAdornment: <InputAdornment position="end">U</InputAdornment> }}
                                    onWheel={event => event.target.blur()}
                                    size='small'
                                    error={lodashGet(params, `row.errors.${params.field}`, false)}
                                />
                            </FormControl>
                        </Box>
                    )
                }
            });

            // Saisie de la dose d'azote déjà apporté
            newColumns.push({
                hearderName: `${StringTranslate.doseAlreadyProvided}`,
                field: 'dosePreviouslyInput',
                minWidth: 150,
                hideable: false,
                disableColumnMenu: true,
                sortable: false,
                renderHeader: (params) => {
                    return (<Typography sx={{ textWrap: 'wrap', textAlign: 'start' }} fontWeight='bold'>{`${StringTranslate.doseAlreadyProvided}*`}</Typography>)
                },
                renderCell: params => {
                    return (
                        <Box
                            style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
                            sx={{ width: "100%" }}>
                            <FormControl>
                                <NumberField
                                    required
                                    id="fumure_ExternalInfos_dosePreviouslyInput"
                                    value={params.value}
                                    onChange={(newValue) => this.handleChangeValue(params.id, params.field, newValue)}
                                    type="uInteger"
                                    InputProps={{ endAdornment: <InputAdornment position="end">U</InputAdornment> }}
                                    onWheel={event => event.target.blur()}
                                    size='small'
                                    error={lodashGet(params, `row.errors.${params.field}`, false)}
                                />
                            </FormControl>
                        </Box>
                    )
                }
            });
        }
        return newColumns;
    }

    /* fonction permettant d'extraire les données qui seront utilisées dans le visuel */
    initialiseDatas(parcelIdFilteredList) {
        const { parcelDico, thumbnailParcelDico, fertilizerDico, } = this.props;
        let parcels = ParcelsHelper.getParcelsDicoFromParcelIdList(parcelDico, parcelIdFilteredList);
        let newDatas = [];

        for (var itemPropName in parcels) {
            const parcel = parcels[itemPropName];

            if (parcel) {
                const thumbnailParcelItem = ParcelsHelper.selectParcelFromDicoById(thumbnailParcelDico, parcel.id);

                let sowingDate = null; //sowingDate,
                let variety = null;
                let isImprovingCrop = false;
                let productionTarget = null;
                let performanceGoal = null;
                let nitrogenPrescription = null;
                let doseSaved = undefined;
                let dosePreviouslyInput = null;

                // Récupération des informations à partir d'un conseil externe existant
                let ferti = lodashFind(Object.values(fertilizerDico), { idParcel: parcel.id, engine: FertilizerEngineEnumValue.EXTERNE, isArchived: false });
                if (ferti) {
                    sowingDate = lodashGet(ferti, 'sowingDate', null);
                    variety = lodashGet(ferti, 'variety', null);
                    if (!variety?.id){
                        variety = null;
                    }
                    isImprovingCrop = lodashGet(ferti, 'isImprovingCrop', false);
                    productionTarget = lodashGet(ferti, 'productionTarget', null);
                    if (!productionTarget?.id){
                        productionTarget = null
                    }
                    performanceGoal = lodashGet(ferti, 'performanceGoal', null);
                    nitrogenPrescription = lodashGet(ferti, 'nitrogenTotal', null);
                    doseSaved = lodashGet(ferti, 'doseSaved', undefined);
                }

                // Si les données sont manquantes, on essaie de les récupérer via les infos de parcelles
                if (sowingDate === null)
                    sowingDate = lodashGet(parcel, 'details.dateSemi', null);

                // Transforme la date sous forme de chaine de caractere en object Date
                if ((!sowingDate) || (!(sowingDate instanceof (Date)))){
                    let newSowingDate = DateHelper.getDateFromString(sowingDate);
                    if (newSowingDate instanceof Date)
                        sowingDate = newSowingDate;
                    else
                        sowingDate = null;
                } 

                //Soit sowingDate vaut null ou alors une instance de date
                if (sowingDate){
                    // Si on a une date, on véirife ça validité
                    // il serait dommage de pré-saisir une date invalide
                    if (sowingDate < this.minSowingDate || sowingDate > this.maxSowingDate)
                        sowingDate = null;
                }

                if (nitrogenPrescription && doseSaved)
                    dosePreviouslyInput = nitrogenPrescription - doseSaved;

                // Récupération des erreurs de saisie pour le dernier apport de cette parcelle
                let errors = lodashGet(this.datasErrors, `${parcel.id}`, null);
                if (!errors){
                    errors = {
                        sowingDate: false, //sowingDate,
                        variety: false,
                        productionTarget: false,
                        performanceGoal: false,
                        nitrogenPrescription: false,
                        dosePreviouslyInput: false,
                    }
                    this.datasErrors[parcel.id] = errors;
                }

                newDatas.push({
                    id: parcel.id,
                    parcel: parcel,
                    thumbnailInfos: thumbnailParcelItem,
                    nom: parcel.name,
                    sowingDate: sowingDate, //sowingDate,
                    variety: variety,
                    isImprovingCrop: isImprovingCrop,
                    productionTarget: productionTarget,
                    performanceGoal: performanceGoal,
                    nitrogenPrescription: nitrogenPrescription,
                    doseSaved: doseSaved,
                    dosePreviouslyInput: dosePreviouslyInput,
                    errors: errors,
                });

            }
        }
        return newDatas;
    }

    /**
     * Fonction permettant de modifier la propriété d'une parcelle vanant d'être saisie
     * @param {number} parcelId 
     * @param {string} valueType 
     * @param {*} value 
     */
    handleChangeValue(parcelId, valueType, value) {
        const { datas } = this.state;
        const { productionTargetsList } = this.props;

        // La valeur venant de changer, on réinitialise l'erreur
        lodashSet(this.datasErrors, `${parcelId}.${valueType}`, false);

        for (const data of datas) {
            if (data.id === parcelId) {
                
                let futureValue = '';
                switch (valueType) {
                    case 'variety':


                        // On change la valeur de ProductionTarget en fonction de la nature de la variété (améliorante ou non)
                        data.isImprovingCrop = lodashGet(value, 'isImprovingCrop', false);
                        let prodTargetRendement = lodashFind(productionTargetsList, (pt) => pt.code === CodeObjectifRendementDefault);
                        let prodTargetProteine = lodashFind(productionTargetsList, (pt) => pt.code === CodeObjectifRendementProtein);
                        if (data.isImprovingCrop){
                            if (prodTargetProteine){
                                data.productionTarget = prodTargetProteine;
                            }
                        }
                        else {
                            if (prodTargetRendement){
                                data.productionTarget = prodTargetRendement;
                            }
                        }

                        // Comme la valeur de productionTarget a changé alors on réinitialise l'erreur
                        lodashSet(this.datasErrors, `${parcelId}.productionTarget`, false);

                        futureValue = value;    // les autoComplete
                        break;
                    case 'productionTarget':
                        futureValue = value;    // les autoComplete
                        break;
                    case 'sowingDate':
                        //futureValue = new Date(value);
                        futureValue = DateHelper.getDateValid(value);
                        break;
                    case "performanceGoal":
                    case "nitrogenPrescription":
                        //On aurai pû vérifier qu'il n'y a QUE des chiffres de saisie (pas de virgule, ni point... de décimal), pour la saisie d'entier,
                        // mais il est plus simple de les convertir au moment de les exploiter dans les algos !
                        //futureValue = parseInt(value.currentTarget.value); //Commentaire à conserver !

                        // MAJ du 10/03/2025: Maintenant on utilise le composant NumberField qui nous renvoie soit un number soit null
                        futureValue = value;
                        break;
                    case "dosePreviouslyInput": 
                        futureValue = value;
                        if (futureValue && data.nitrogenPrescription && stringHelper.isUnsignedInteger(data.nitrogenPrescription)) {
                            data.doseSaved = data.nitrogenPrescription - futureValue;
                        }
                        else 
                        data.doseSaved = undefined;
                        break;
                    default:
                        futureValue = value;
                        break;
                }

                data[valueType] = futureValue;
            }
        }

        this.setState({
            datas: datas,
        });
    }

    /**
     * Fonction appelé au click sur le bonton de lancement de calcul
     * Permet la sauvegarde des données puis le lancement du calcul du dernier apport
     */
    saveSpecificInfoAndBuildLastNInputs() {
        const { datas } = this.state;
        const { saveExternalSpecificInfos } = this.props;

        let datasToManage = [];

        // Réinitialise les messages d'erreurs
        this.errorMessages = {
            missingData: false,
            errorSowingDate: false,
            errorPerformanceGoal: false,
            errorDoseTotal: false,
            errorDosePreviouslyInput: false,
        }
        
        for (const data of datas) {
            // Vérification de la validité des données
            let isValid = this.controlData(data);
            if (isValid) {
                datasToManage.push({
                    // infos client et parcelle
                    // id: 0,                                                   // à revoir
                    idClient: lodashGet(data, 'parcel.clientId', 0),
                    idParcel: data.id,
                    // codeCity: le nouveau code INSEE
        
                    enumOfCrop: enumCultureFertilizer.ble_hiver, // identifiant de culture Spotifarm, type 'enumCultureFertilizer' !
                    crop: undefined, // Identifiant de culture associé au moteur de fumure (sera renseigné dans le back)
                    isImprovingCrop: lodashGet(data, 'variety.isImprovingCrop', false),
                    engine: FertilizerEngineEnum.EXTERNE,
                    variety: data.variety,
                    sowingDate: data.sowingDate,
                    productionTarget: data.productionTarget,
                    performanceGoal: data.performanceGoal,
                    nitrogenTotal: data.nitrogenPrescription,
                    doseSaved: data.doseSaved,
                });
            }
        }

        let generateResults = (datasToManage.length > 0);

        // indique si des derniers apports sont en cours de génération et s'il y a des saisies incorrectes
        this.setState({
            showErrorMessages: Object.values(this.errorMessages).some(errorMessage => errorMessage === true),
            generateResults: generateResults,
        });

        // lance la sauvegarde et le calcul de dernier apport
        if (generateResults) {
            saveExternalSpecificInfos(datasToManage);
        }
    }

    /**
     * Vérifie la validité des données
     * @param {*} data Données saisies par le client pour une parcelle
     * @returns {boolean} si toutes les données sont valides
     */
    controlData(data) {
        let isValid = true;

        // Variété
        // Doit être saisie
        if (lodashIsNil(data.variety))
        {
            this.errorMessages.missingData = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.variety`, true);
        }

        // Objectif de Production
        // Doit être saisi
        if (lodashIsNil(data.productionTarget))
        {
            this.errorMessages.missingData = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.productionTarget`, true);
        }

        // Date de Semi
        // Date de l'année dernière entre le mois d'aout et le mois de décembre
        if ((lodashIsNil(data.sowingDate)) || (!(data.sowingDate instanceof Date))){
            this.errorMessages.missingData = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.sowingDate`, true);
        }
        else if ((data.sowingDate < this.minSowingDate) || (data.sowingDate > this.maxSowingDate))
        {
            this.errorMessages.errorSowingDate = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.sowingDate`, true);
        }
            

        // Rendement
        // Entier positif entre 0 et 140
        let performanceGoalValue = parseInt(data.performanceGoal);
        if (lodashIsNil(data.performanceGoal) || isNaN(performanceGoalValue)){
            this.errorMessages.missingData = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.performanceGoal`, true);
        }
        else if ((!stringHelper.isUnsignedInteger(data.performanceGoal))
            || (performanceGoalValue < this.minOlympicPerformance) || (performanceGoalValue > this.maxOlympicPerformance))
        {
            this.errorMessages.errorPerformanceGoal = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.performanceGoal`, true);
        }

        // Dose PPF
        // Entier positif entre 0 et 300
        let nitrogenPrescriptionValue = parseInt(data.nitrogenPrescription);
        if (lodashIsNil(data.nitrogenPrescription) || isNaN(nitrogenPrescriptionValue)){
            this.errorMessages.missingData = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.nitrogenPrescription`, true);
        } 
        else if ((!stringHelper.isUnsignedInteger(data.nitrogenPrescription))
            || (nitrogenPrescriptionValue < this.minDoseTotal) || (nitrogenPrescriptionValue > this.maxDoseTotal))
        {
            this.errorMessages.errorDoseTotal = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.nitrogenPrescription`, true);
        }

        // Dose déjà apporté (dose de réserve)
        // Entier positif inférieur à la Dose PPF
        let dosePreviouslyInputValue = parseInt(data.dosePreviouslyInput);
        if (lodashIsNil(data.dosePreviouslyInput) || isNaN(dosePreviouslyInputValue)){
            this.errorMessages.missingData = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.dosePreviouslyInput`, true);
        }
        else if ((!stringHelper.isUnsignedInteger(data.dosePreviouslyInput))
            || (dosePreviouslyInputValue > nitrogenPrescriptionValue))
        {
            this.errorMessages.errorDosePreviouslyInput = true;
            isValid = false;
            lodashSet(this.datasErrors, `${data.id}.dosePreviouslyInput`, true);
        }

        return isValid;
    }

    /* Ferme le dialog des messages d'erreur */
	closeDialogOfErrors = () => {
		const { clearLastNitrogenInputError } = this.props;

		this.setState({
			openDialogOfErrors: false,
		});

		clearLastNitrogenInputError();
	}

    /**
     * Fonction permettant de changer et vérifie la validité de la saisie commune du rendement olympique
     * @param {number} newValue 
     */
    onChangeCommonPerformance = (newValue) => {
        let newCommonPerformanceError = (newValue < this.minOlympicPerformance) || (newValue > this.maxOlympicPerformance);

        this.setState({
            commonPerformance: newValue,
            commonPerformanceError: newCommonPerformanceError,
        });
        
    }

    /**
     * Fonction permettand d'appliquer la valeur de rendement commune à toutes les parcelles
     */
    applyCommonPerformanceToAll = () => {
        const { commonPerformance, datas } = this.state;

        for (const data of datas) {
            data.performanceGoal = commonPerformance;
        }

        this.setState({
            datas: datas,
        });
    }

    /**
     * Fonction permettant d'appliquer la valeur de rendement commune uniquement si celui des parcelles n'est pas définie
     */
    applyCommonPerformanceIfNotDefined = () => {
        const { commonPerformance, datas } = this.state;

        for (const data of datas) {
            // Si data.performanceGoal est null ou undefined alors on applique commonPerformance
            data.performanceGoal ??= commonPerformance;
        }

        this.setState({
            datas: datas,
        });
    }

    //Toolbar customisé du tableau comprenant :
    // - la zone de recherche
    // - le bouton pour commencer la saisi des infos
    CustomToolbar = (props) => {
        const { showProfilMenuDialog, loading, saveExternalSpecificInfos, showErrorMessages, showFindResultMessage,
            commonPerformance, onChangeCommonPerformance, applyCommonPerformanceToAll, applyCommonPerformanceIfNotDefined, commonPerformanceError,
         } = props;
        return (
            <Grid container spacing={2} sx={{ mt: 2 }}>
                {/* Zone de recherche / zone du bouton de validation*/}
                <Grid item xs={12} sx={{ mt: 2 }}>
                    <Grid container spacing={1}>
                        {/* Zone de recherche */}
                        <Grid item xs={6} sm={8} md={8}>
                            <Box
                                sx={{ display: { xs: 'block', md: 'flex' }, alignItems: 'center' }}
                            >
                                <NumberField
                                    size='small'
                                    sx={{ my: 1 }}
                                    label={StringTranslate.olympicPerformance}
                                    type='uInteger'
                                    InputProps={{ endAdornment: <InputAdornment position="end">q/ha</InputAdornment> }}
                                    value={commonPerformance}
                                    onChange={(value) => onChangeCommonPerformance(value)}
                                    error={commonPerformanceError}
                                />
                                <Button
                                    sx={{ m: { xs:0, md: 1} }}
                                    variant="outlined" color="primary"
                                    onClick={applyCommonPerformanceToAll}
                                    disabled={(commonPerformance === null) || commonPerformanceError}
                                >
                                    {StringTranslate.applyCommonPerformanceToAll}
                                </Button>
                                <Button
                                    sx={{ my: 1 }}
                                    variant="outlined" color="primary"
                                    onClick={applyCommonPerformanceIfNotDefined}
                                    disabled={(commonPerformance === null) || commonPerformanceError}
                                >
                                    {StringTranslate.applyCommonPerformanceIfNotDefined}
                                </Button>
                            </Box>
                        </Grid>

                        {/* Boutons d'actions */}
                        <Grid item xs={6} sm={4} md={4}>
                            {/* Boutons en écran PC (>= md) */}
                            <Box sx={{ display: { md: 'block', xs: 'none' }, '& button': { m: 1 } }} style={{ textAlign: 'end' }} >
                                <Button
                                    variant="contained" color="primary"
                                    disabled={loading}
                                    onClick={saveExternalSpecificInfos}
                                    startIcon={(loading) ? <CircularProgress size={20} color="inherit" /> : <Calculate />}
                                >
                                    {StringTranslate.buildLastInputNs}
                                </Button>
                            </Box>
                            {/* Boutons en écran Mobile (< md) */}
                            <Stack 
                                sx={{ display: { md: 'none', xs: 'block' }, '& button': { m: 1 }, pt:1 }} 
                                style={{ textAlign: 'center' }} 
                                direction="row" spacing={1}
                            >
                                <Button
                                    disabled={loading}
                                    variant="contained" color="primary"
                                    size="medium"
                                    aria-label="build Last input(s)"
                                    component="span"
                                    onClick={saveExternalSpecificInfos}
                                    startIcon={(loading === true) ? <CircularProgress size={20} color="inherit" /> : <Calculate />}
                                >
                                    {StringTranslate.startCalculation}
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Message d'erreur de la saisie commune du rendement olympique */}
                {(commonPerformanceError) && 
                    <Grid item xs={12}>
                        <Typography color="error">
                            {StringTranslate.formatString(StringTranslate.errorOlympicPerformanceForLastInput, this.maxOlympicPerformance)}
                        </Typography>
                    </Grid>
                }

                {/* Bouton d'aide */}
                <Grid item xs={12}>
                    <Button color="secondary" variant="text" size="small" onClick={() => showProfilMenuDialog(ProfilIndex.aide_LastNitrogenInput)}>
                        {StringTranslate.helpAsk}
                    </Button>
                </Grid>
                {/* Données non remplies pour démarrer le calcul */}
                {(showErrorMessages === true) &&
                    <Grid item xs={12}>
                        <Card>
                            <Alert
                                severity="error"
                            >
                                {((this.errorMessages.missingData) && <Typography>{StringTranslate.haveToFillAllDataForLastInput}</Typography>)}
                                {((this.errorMessages.errorSowingDate) && <Typography>{StringTranslate.formatString(StringTranslate.errorSowingDateForLastInput, this.minSowingDateString, this.maxSowingDateString)}</Typography>)}
                                {((this.errorMessages.errorPerformanceGoal) && <Typography>{StringTranslate.formatString(StringTranslate.errorOlympicPerformanceForLastInput, this.maxOlympicPerformance)}</Typography>)}
                                {((this.errorMessages.errorDoseTotal) && <Typography>{StringTranslate.formatString(StringTranslate.errorDoseTotalForLastInput, this.maxDoseTotal)}</Typography>)}
                                {((this.errorMessages.errorDosePreviouslyInput) && <Typography>{StringTranslate.errorDosePreviouslyInput}</Typography>)}
                            </Alert>
                        </Card>
                    </Grid>
                }

                {/* Indique où trouver les derniers apports ayant été calculés */}
                {(showFindResultMessage === true) &&
                    <Grid item xs={12}>
                        <Card>
                            <Alert
                                severity="info"
                                icon={<InfoOutlined />}
                            >
                                <Typography>{StringTranslate.findLastInputCalculated}</Typography>
                            </Alert>
                        </Card>
                    </Grid>
                }
            </Grid>
        );
    }

    render() {
        const { datas, showErrorMessages, showFindResultMessage, openDialogOfErrors, commonPerformance, commonPerformanceError, } = this.state;
        const { goBack, rowsPerPageForTable, showProfilMenuDialog, loadingVarietiesList, loadingCommonLists, building, } = this.props;

        return (
            <>
                {/* ↓↓ Partie visuel - affichage Pop d'une erreur ↓↓ */}
                {(openDialogOfErrors === true) && (
                    <AlertDialog popup={this.popupErrorDialog} handleChangePopup={this.closeDialogOfErrors} />
                )}

                <TableContainer>
                    <Grid style={{ width: '100%' }} item xs={12}>
                        {/* partie tableau */}
                        <CustomDataGrid
                            loading={building}
                            disableSelectionOnClick={true}
                            tableName={TableType.fertilizers}
                            pageSize={rowsPerPageForTable}
                            rows={datas}
                            columns={this.columns}
                            Toolbar={this.CustomToolbar}
                            toolbar={{
                                saveExternalSpecificInfos: () => this.saveSpecificInfoAndBuildLastNInputs(),
                                loading: ((loadingVarietiesList === true) || (loadingCommonLists === true) || (building)),

                                // Accès menu d'aide
                                showProfilMenuDialog: (index) => showProfilMenuDialog(index),

                                showErrorMessages: showErrorMessages,
                                showFindResultMessage: showFindResultMessage,

                                // Saisie commune du rendement olympique
                                commonPerformance: commonPerformance,
                                onChangeCommonPerformance: this.onChangeCommonPerformance,
                                applyCommonPerformanceToAll: this.applyCommonPerformanceToAll,
                                applyCommonPerformanceIfNotDefined: this.applyCommonPerformanceIfNotDefined,
                                commonPerformanceError: commonPerformanceError,
                            }}
                        />
                    </Grid>
                </TableContainer>
                
                {/* Boutons :
                 - retour vers l'étape 1 (choix des parcelles) */}
                <Grid container marginTop={3}>
                    <Grid item xs={4} sm={4}>
                        <Button 
                            variant="text" 
                            onClick={goBack}
                        >
                            {StringTranslate.revenir}
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4}>

                    </Grid>

                    <Grid item xs={12} sm={4} sx={{ [theme.breakpoints.only('xs')]: { marginTop: 1 } }}>

                    </Grid>
                </Grid>
            </>
        );
    }
}

function mapStateToProps(state) {
    return {
        parcelDico: lodashGet(state, 'parcelsData.parcelDico', {}),
        thumbnailParcelDico: lodashGet(state, 'parcelsData.thumbnailParcelDico', {}),

        parcelIdsSelected: lodashGet(state, 'lastNitrogenInputData.parcelIdsSelectedForExternalInfos', []),
        building: lodashGet(state, 'lastNitrogenInputData.building', false),
        errorMessage: lodashGet(state, 'lastNitrogenInputData.errorMessage', undefined),

        language: lodashGet(state, 'settingsData.settings.language', StringTranslate.getLanguage()), // Permet de raffraichir le composant lors d'un changement de langue
        rowsPerPageForTable: lodashGet(state, 'settingsData.settings.rowsPerPageForTableParcels', 20),

        fertilizerDico: lodashGet(state, 'fertilizerData.fertilizerDico', {}),
        productionTargetsList: lodashGet(state, 'fertilizerData.fertilizerCommonLists.productionTargets', []),
        loadingCommonLists: lodashGet(state, 'fertilizerData.askingCommonInfosAndListsByCulture', false),
        varietiesList: lodashGet(state, `fertilizerData.fertilizerSpecificList_VarieteDico[${FertilizerEngineEnum.EXTERNE}]`, []),
        loadingVarietiesList: lodashGet(state, 'fertilizerData.fertilizerSpecificList_Variete_awaitingAnAnswer', false),
        closingDateAzofert: lodashGet(state, 'fertilizerData.closingDateAzofert', ConstantsFertilizer.DefaultClosingDateAzofert),
        openingDateAzofert: lodashGet(state, 'fertilizerData.openingDateAzofert', ConstantsFertilizer.DefaultOpeningDateAzofert),
    };
}

function mapDispatchToProps(dispatch) {
    return {
        clearLastNitrogenInputError: () => dispatch(ActionClearLastNitrogenInputError()),
        goBack: () => dispatch(ActionGoToExternalStep(LastNitrogenInputExternalSteps.PARCEL_CHOICE)),

        saveExternalSpecificInfos: (specificInfos) => dispatch(ActionSaveExternalSpecificInfos(specificInfos)),

        showProfilMenuDialog: (index) => dispatch( ActionShowProfilMenuDialog(index) ),

        loadVaritiesFromParcel: (idParcel) =>  dispatch(ActionLoadVaritiesForExternalSpecificInfosFromParcel(idParcel))
    };
}

export default connect(mapStateToProps,mapDispatchToProps)(LastNitrogenInputExternalInfosManagement);